/**
 * usePlayerEngine
 * 
 * 职责：统一封装所有 video element 操作，消除 UI 组件直接操作 videoRef 的行为
 * - 提供 play/pause/togglePlay/seek/setPlaybackRate 等 actions
 * - 所有操作都通过这个 hook 进行，UI 组件不再直接访问 videoRef
 */

import { useCallback, useMemo, useRef } from 'react';

export interface PlayerActions {
  play: () => Promise<void>;
  pause: () => void;
  togglePlay: () => Promise<void>;
  seek: (
    time: number,
    options?: {
      /** seek 前若在播，seek 完成后恢复播放（进度条等场景） */
      autoPlayAfterSeek?: boolean;
      /** 用户手势触发的跳转（如点字幕）：无论 seek 前是否暂停，seek 完成后都尝试 play */
      forcePlayAfterSeek?: boolean;
      onSeeked?: () => void;
    }
  ) => void;
  setPlaybackRate: (rate: number) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getIsPaused: () => boolean;
}

interface UsePlayerEngineParams {
  videoRef: React.RefObject<HTMLVideoElement | null>;
}

/**
 * 安全的 seek 函数：确保 HLS 视频可靠地跳转到指定时间
 */
function safeSeek(
  video: HTMLVideoElement,
  targetTime: number,
  onSeeked?: () => void
): void {
  // 检查时间有效性
  if (typeof targetTime !== 'number' || isNaN(targetTime) || targetTime < 0) {
    return;
  }

  // 检查时长是否有效
  if (video.duration && targetTime > video.duration) {
    return;
  }

  // 【防翻车检查点2】HLS 视频需要等待 canplay 事件才能可靠地 seek
  // readyState < 3 (HAVE_FUTURE_DATA) 表示数据不足，需要等待
  if (video.readyState < 3) {
    const onCanPlay = () => {
      video.removeEventListener('canplay', onCanPlay);
      // 【防翻车检查点2】再次检查 video 是否还存在（可能已被销毁）
      if (!video || !document.contains(video)) {
        // video 已被销毁，静默失败，避免 UI 崩溃
        if (onSeeked) {
          onSeeked();
        }
        return;
      }
      // 再次检查时长（可能在等待期间发生了变化）
      if (video.duration && targetTime > video.duration) {
        if (onSeeked) {
          onSeeked();
        }
        return;
      }
      
      // 使用 seeked 事件确保 seek 完成
      const onSeekedHandler = () => {
        video.removeEventListener('seeked', onSeekedHandler);
        if (onSeeked) {
          onSeeked();
        }
      };
      
      try {
        video.addEventListener('seeked', onSeekedHandler, { once: true });
        video.currentTime = targetTime;
      } catch (error) {
        // 【防翻车检查点2】seek 失败时静默处理，避免 UI 崩溃
        console.warn('Seek 失败（canplay 后）:', error);
        // 仍然调用回调，避免 UI 等待
        if (onSeeked) {
          onSeeked();
        }
      }
    };
    try {
      video.addEventListener('canplay', onCanPlay, { once: true });
    } catch (error) {
      // 【防翻车检查点2】监听器添加失败时静默处理
      console.warn('添加 canplay 监听器失败:', error);
      // 仍然调用回调，避免 UI 等待
      if (onSeeked) {
        onSeeked();
      }
    }
    return;
  }

  // 视频已准备好，直接 seek
  try {
    // 使用 seeked 事件确保 seek 完成
    const onSeekedHandler = () => {
      video.removeEventListener('seeked', onSeekedHandler);
      if (onSeeked) {
        onSeeked();
      }
    };
    
    video.addEventListener('seeked', onSeekedHandler, { once: true });
    video.currentTime = targetTime;
  } catch (error) {
    // 【防翻车检查点2】设置视频时间失败，静默失败避免 UI 崩溃
    console.warn('Seek 失败（readyState >= 3）:', error);
    // 仍然调用回调，避免 UI 等待
    if (onSeeked) {
      onSeeked();
    }
  }
}

export function usePlayerEngine({ videoRef }: UsePlayerEngineParams): PlayerActions {
  const play = useCallback(async (): Promise<void> => {
    const video = videoRef.current;
    if (!video) return;
    try {
      await video.play();
    } catch (error) {
      // 播放失败，忽略错误（可能是用户手势要求）
    }
  }, [videoRef]);

  const pause = useCallback((): void => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
  }, [videoRef]);

  const togglePlay = useCallback(async (): Promise<void> => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      await play();
    } else {
      pause();
    }
  }, [videoRef, play, pause]);

  const seek = useCallback((
    time: number,
    options?: {
      autoPlayAfterSeek?: boolean;
      forcePlayAfterSeek?: boolean;
      onSeeked?: () => void;
    }
  ): void => {
    const video = videoRef.current;
    if (!video) {
      // 【防翻车检查点2】video 不存在时静默失败，避免 UI 崩溃
      if (options?.onSeeked) {
        options.onSeeked();
      }
      return;
    }

    const { autoPlayAfterSeek = false, forcePlayAfterSeek = false, onSeeked } = options || {};

    // 记录 seek 前的播放状态（需要在 video 存在时记录）
    const wasPlaying = !video.paused;

    const resumeIfRequested = (): void => {
      if (forcePlayAfterSeek || (autoPlayAfterSeek && wasPlaying)) {
        play().catch(() => {
          // 播放失败，忽略错误（可能是用户手势要求）
        });
      }
    };

    // 【防翻车检查点2】检查视频是否已加载元数据（Safari 等浏览器需要）
    // 如果连 metadata 都没加载（readyState === 0），先等待 loadedmetadata 事件
    if (video.readyState === 0) {
      const onLoadedMetadata = () => {
        video.removeEventListener('loadedmetadata', onLoadedMetadata);
        // 递归调用 seek，此时 readyState 应该 > 0
        seek(time, options);
      };
      try {
        video.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
      } catch (error) {
        // 【防翻车检查点2】监听器添加失败时静默处理，仍然尝试直接 seek
        console.warn('添加 loadedmetadata 监听器失败:', error);
        // 直接调用 safeSeek，内部会处理异常
        safeSeek(video, time, () => {
          if (onSeeked) {
            onSeeked();
          }
          resumeIfRequested();
        });
      }
      return;
    }

    // 执行 seek（safeSeek 内部已处理 canplay 等待和异常）
    safeSeek(video, time, () => {
      // seek 完成后的回调
      if (onSeeked) {
        onSeeked();
      }
      resumeIfRequested();
    });
  }, [videoRef, play]);

  const setPlaybackRate = useCallback((rate: number): void => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = rate;
  }, [videoRef]);

  const getCurrentTime = useCallback((): number => {
    const video = videoRef.current;
    return video ? video.currentTime : 0;
  }, [videoRef]);

  const getDuration = useCallback((): number => {
    const video = videoRef.current;
    return video ? video.duration : 0;
  }, [videoRef]);

  const getIsPaused = useCallback((): boolean => {
    const video = videoRef.current;
    return video ? video.paused : true;
  }, [videoRef]);

  // 【防翻车检查点1】使用 useMemo 确保 actions 对象引用稳定
  // 避免每次 render 创建新对象，导致下游组件/Hook 依赖变更、memo 失效、effect 频繁重跑
  return useMemo(() => ({
    play,
    pause,
    togglePlay,
    seek,
    setPlaybackRate,
    getCurrentTime,
    getDuration,
    getIsPaused,
  }), [play, pause, togglePlay, seek, setPlaybackRate, getCurrentTime, getDuration, getIsPaused]);
}

